import { useState } from 'react';
import { revisionIndex, revisionsInOrder, type Id, type Project } from '@elec/domain';
import { localeTag as uiTag, type Locale, type Translate } from '@elec/i18n';
import { Dialog } from './ui.tsx';

/** Revision list: create the next index, compare a revision with now, or go back to it. */
export function RevisionsDialog(props: {
  project: Project;
  t: Translate;
  locale: Locale;
  onClose(): void;
  onCreate(description: string): void;
  /** Show the changes from a revision to another one, or to now (`to` null). */
  onCompare(from: Id, to: Id | null): void;
  /** The changes as a PDF (the list, then the sheets that changed, outlined). */
  onChangesPdf(from: Id, to: Id | null): void;
  onRestore(id: Id): void;
}) {
  const { t, project } = props;
  const revs = revisionsInOrder(project);
  const next = revisionIndex(revs.length);
  const [text, setText] = useState('');
  const [from, setFrom] = useState<Id>(revs.at(-2)?.id ?? revs[0]?.id ?? '');
  const [to, setTo] = useState<Id | ''>(revs.length > 1 ? (revs.at(-1)?.id ?? '') : '');
  const date = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString(uiTag(props.locale));
  return (
    <Dialog
      title={t('rev.title')}
      wide
      onClose={props.onClose}
      actions={<button onClick={props.onClose}>{t('dialog.close')}</button>}
    >
      {revs.length === 0 ? (
        <p className="muted">{t('rev.none')}</p>
      ) : (
        <div className="table-scroll">
          <table data-testid="rev-table">
            <thead>
              <tr>
                <th>{t('rev.index')}</th>
                <th>{t('rev.date')}</th>
                <th>{t('rev.author')}</th>
                <th>{t('rev.description')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {revs.map((r) => (
                <tr key={r.id}>
                  <td className="code">{r.index}</td>
                  <td className="nowrap">{date(r.date)}</td>
                  <td>{r.author}</td>
                  <td>{r.description}</td>
                  <td className="nowrap">
                    <button
                      className="link"
                      onClick={() => props.onCompare(r.id, null)}
                      data-testid={`rev-compare-${r.index}`}
                    >
                      {t('rev.compare')}
                    </button>{' '}
                    <button
                      className="link"
                      onClick={() => props.onRestore(r.id)}
                      data-testid={`rev-restore-${r.index}`}
                    >
                      {t('rev.restore')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {revs.length > 0 && (
        <>
          <h3 className="dialog-sub">{t('rev.between')}</h3>
          <div className="rev-between">
            <label>
              {t('rev.from')}{' '}
              <select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="rev-from">
                {revs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.index} — {date(r.date)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('rev.to')}{' '}
              <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="rev-to">
                {revs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.index} — {date(r.date)}
                  </option>
                ))}
                <option value="">{t('rev.now')}</option>
              </select>
            </label>
            <button onClick={() => props.onCompare(from, to || null)} data-testid="rev-show">
              {t('rev.show')}
            </button>
            <button onClick={() => props.onChangesPdf(from, to || null)} data-testid="rev-pdf">
              {t('rev.pdf')}
            </button>
          </div>
        </>
      )}
      <h3 className="dialog-sub">{t('rev.new')}</h3>
      <p className="small">{t('rev.newHint', { index: next })}</p>
      <div className="chat-input">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('rev.description')}
          style={{ flex: 1 }}
          data-testid="rev-text"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && text.trim()) props.onCreate(text.trim());
          }}
        />
        <button
          className="primary"
          disabled={!text.trim()}
          onClick={() => props.onCreate(text.trim())}
          data-testid="rev-create"
        >
          {t('rev.create', { index: next })}
        </button>
      </div>
    </Dialog>
  );
}

/** Ask for a short name. */
export function NameDialog(props: {
  title: string;
  label: string;
  initial?: string;
  ok: string;
  cancel: string;
  onClose(): void;
  onOk(name: string): void;
}) {
  const [v, setV] = useState(props.initial ?? '');
  return (
    <Dialog
      title={props.title}
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{props.cancel}</button>
          <button
            className="primary"
            disabled={!v.trim()}
            onClick={() => props.onOk(v.trim())}
            data-testid="name-ok"
          >
            {props.ok}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="name-input">{props.label}</label>
        <input
          id="name-input"
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && v.trim() && props.onOk(v.trim())}
          data-testid="name-input"
        />
      </div>
    </Dialog>
  );
}
